import React, { useEffect, useRef, useState } from "react";
import { Siren, Radio, FolderOpen, ExternalLink, Loader2, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { playPing } from "@/lib/sound";
import { useLang } from "@/context/LangContext";
import { LangMenu } from "@/components/common/LangMenu";
import { ReadMark, TypingDots, LiveComposer, useAutoScroll, fmtTime } from "@/components/live/LiveParts";

const TX = {
  hu: { title: "Emergency line", sub: "Élő, titkosított csatorna a moderátorral", typing: "Az ügyintéző ír", ph: "Írja ide az üzenetét…", send: "Küldés", case: "Kapcsolódó ügy", gone: "Ez a beszélgetés lezárult vagy nem érhető el.", you: "Ön", agent: "Ügyintéző", empty: "A kapcsolat létrejött. Az ügyintéző hamarosan ír.", live: "ÉLŐ", open: "Ügy megnyitása" },
  en: { title: "Emergency line", sub: "Live, secure channel with a moderator", typing: "Agent is typing", ph: "Type your message…", send: "Send", case: "Linked case", gone: "This conversation has ended or is not available.", you: "You", agent: "Agent", empty: "Connection established. The agent will write shortly.", live: "LIVE", open: "Open case" },
  de: { title: "Emergency line", sub: "Live- und sicherer Kanal mit einem Moderator", typing: "Moderator schreibt", ph: "Nachricht eingeben…", send: "Senden", case: "Verknüpfter Fall", gone: "Dieses Gespräch ist beendet oder nicht verfügbar.", you: "Sie", agent: "Moderator", empty: "Verbindung hergestellt. Der Moderator schreibt gleich.", live: "LIVE", open: "Fall öffnen" },
  fr: { title: "Emergency line", sub: "Canal sécurisé en direct avec un modérateur", typing: "Le modérateur écrit", ph: "Saisissez votre message…", send: "Envoyer", case: "Dossier lié", gone: "Cette conversation est terminée ou indisponible.", you: "Vous", agent: "Modérateur", empty: "Connexion établie. Le modérateur va écrire.", live: "EN DIRECT", open: "Ouvrir le dossier" },
  cs: { title: "Emergency line", sub: "Živý zabezpečený kanál s moderátorem", typing: "Moderátor píše", ph: "Napište zprávu…", send: "Odeslat", case: "Propojený případ", gone: "Tato konverzace skončila nebo není dostupná.", you: "Vy", agent: "Moderátor", empty: "Spojení navázáno. Moderátor brzy napíše.", live: "ŽIVĚ", open: "Otevřít případ" },
};

function Backdrop() {
  return (
    <>
      <div className="pointer-events-none fixed inset-0 bg-[#070203]" />
      <div className="pointer-events-none fixed inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#ef4444 1px, transparent 1px), linear-gradient(90deg, #ef4444 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
      <div className="pointer-events-none fixed -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-red-700/20 blur-[120px]" />
      <div className="pointer-events-none fixed inset-0" style={{ background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.25) 0 1px, transparent 1px 3px)" }} />
    </>
  );
}

function CaseCard({ r, x, lang }) {
  if (!r) return null;
  return (
    <div className="rounded-xl border border-red-900/50 bg-black/50 p-4 backdrop-blur" data-testid="live-case">
      <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-red-400"><FolderOpen size={13} /> {x.case}</div>
      <div className="font-mono text-[15px] text-zinc-50">{r.reference}</div>
      <div className="mt-1 text-[12px] text-zinc-400">{r.platform} · <span style={{ color: r.status_color || "#f87171" }}>{r.status_labels?.[lang] || r.status_labels?.en || r.status}</span></div>
      {r.content_url && <div className="mt-2 break-all font-mono text-[11px] text-zinc-500">{r.content_url}</div>}
      {r.decision_token && <a href={`/decision/${r.decision_token}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[12px] text-red-300 hover:text-red-200">{x.open} <ExternalLink size={12} /></a>}
    </div>
  );
}

export default function LiveChatPage() {
  const token = window.location.pathname.split("/").pop();
  const { lang } = useLang();
  const x = TX[lang] || TX.en;
  const [chat, setChat] = useState(null);
  const [gone, setGone] = useState(false);
  const seen = useRef(null);
  const draftTimer = useRef(null);
  const scrollRef = useAutoScroll(chat?.messages?.length);

  useEffect(() => {
    let alive = true;
    const tick = () => api.get(`/livechat/${token}`).then((r) => {
      if (!alive) return;
      const agentMsgs = r.data.messages.filter((m) => m.sender === "agent").length;
      if (seen.current !== null && agentMsgs > seen.current) playPing();
      seen.current = agentMsgs;
      setChat(r.data);
    }).catch((e) => { if (e?.response?.status === 404) setGone(true); });
    tick();
    const id = setInterval(tick, 1000);
    return () => { alive = false; clearInterval(id); };
  }, [token]);

  const onType = (text) => {
    clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => api.post(`/livechat/${token}/draft`, { text }).catch(() => {}), 250);
  };
  const send = async (text) => {
    const r = await api.post(`/livechat/${token}/messages`, { text });
    setChat((c) => ({ ...c, messages: [...c.messages, r.data] }));
  };

  return (
    <div className="relative min-h-screen text-zinc-100" style={{ fontFamily: "'Chakra Petch', Inter, sans-serif" }} data-testid="live-page">
      <Backdrop />
      <div className="relative mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-6 sm:px-6">
        <header className="mb-6 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl border border-red-500/40 bg-red-600/15 text-red-400 shadow-[0_0_30px_rgba(239,68,68,0.35)]"><Siren size={20} /></div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold uppercase tracking-[0.2em] text-zinc-50 sm:text-3xl">{x.title}</h1>
            <p className="text-[12px] tracking-wide text-red-300/70">{x.sub}</p>
          </div>
          {!gone && <span className="hidden items-center gap-1.5 rounded-full border border-red-500/50 px-3 py-1 text-[10px] font-bold tracking-[0.25em] text-red-300 sm:inline-flex"><Radio size={12} className="animate-pulse" /> {x.live}</span>}
          <LangMenu />
        </header>

        {gone ? (
          <div className="grid flex-1 place-items-center" data-testid="live-gone">
            <div className="text-center"><ShieldAlert size={40} className="mx-auto text-red-500/70" /><p className="mt-4 text-[15px] text-zinc-400">{x.gone}</p></div>
          </div>
        ) : !chat ? (
          <div className="grid flex-1 place-items-center"><Loader2 className="animate-spin text-red-500" /></div>
        ) : (
          <div className="grid flex-1 gap-4 lg:grid-cols-[1fr_280px]">
            <section className="flex min-h-[60vh] flex-col overflow-hidden rounded-2xl border border-red-900/50 bg-black/40 backdrop-blur-xl">
              <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-5" data-testid="live-messages">
                {chat.messages.length === 0 && <p className="py-10 text-center text-[13px] text-zinc-500">{x.empty}</p>}
                {chat.messages.map((m) => {
                  const mine = m.sender === "user";
                  return (
                    <div key={m.id} className={`flex animate-in fade-in slide-in-from-bottom-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`live-msg-${m.id}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "rounded-br-sm border border-red-500/40 bg-red-600/20" : "rounded-bl-sm border border-zinc-800 bg-zinc-900/80"}`}>
                        <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">{mine ? x.you : `${x.agent} · ${m.author}`}</div>
                        <div className="whitespace-pre-wrap text-[14px] leading-relaxed">{m.text}</div>
                        <div className="mt-1 flex items-center justify-end gap-1 font-mono text-[10px] text-zinc-500">{fmtTime(m.created_at)} {mine && <ReadMark m={m} />}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-red-900/40 p-4">
                <div className="mb-2 h-4">{chat.agent_typing && <TypingDots label={x.typing} />}</div>
                <LiveComposer onSend={send} onType={onType} placeholder={x.ph} sendLabel={x.send} />
              </div>
            </section>
            <aside className="space-y-4"><CaseCard r={chat.report} x={x} lang={lang} /></aside>
          </div>
        )}
      </div>
    </div>
  );
}
